import { Typography } from '@mui/material';
import React, { useCallback, useState } from 'react';
import { Path } from 'react-hook-form';
import { useHistory, useParams } from 'react-router-dom';
//
import { paths } from '../../config';
import { useToast } from '../../context/ToastContext';
import { FormInputProps } from '../../componets/form/formInterfaces';
import IDescription from '../../util/interfaces/description';
import { useAuth } from '../../context/AuthContext';
import FormComplete from '../../componets/form/formComplete';
import * as ImplEnum from '../../communs/enums/generic-enum';
import { ImperativeHandleAccessedByParentComponent } from '../../componets/interfaces/formProps';

interface IFormInput {
    code: string;
    dateHearing: Date | undefined;
    customer?: IDescription;
    user?: IDescription;
    situation?: {
        id: string;
        description: string;
    };
    resultLegalProcess?: {
        id: string;
        description: string;
    };
    valueSentense: number;
    processNumber: string;
    honoraryValue: number | string;
    totalValue: number | string;
    description: string;
    generateInstallments?: boolean;
    valueSentence: number;
    percentageHonoraryValue: number;
    endDate?: Date;
}

interface ICalcTotalValue {
    valueSentence: number;
    percentageHonoraryValue: number;
    honoraryValue: number;
    succumbency: number;
}

interface IGetValue {
    key:
        | 'valueSentence'
        | 'percentageHonoraryValue'
        | 'honoraryValue'
        | 'succumbency'
        | 'generateInstallments'
        | 'totalValue'
        | 'user';
}

interface ICalcPercentual {
    value: number;
    percentual: number;
}

const enumInstances = {
    yesOrNoOptionsInstance: new ImplEnum.GenericEnum(ImplEnum.yesOrNoOptions),
    taskSituationOptionsInstance: new ImplEnum.GenericEnum(
        ImplEnum.taskSituationOptions,
    ),
    statusOptionsInstance: new ImplEnum.GenericEnum(ImplEnum.statusOptions),
    typeLegalProcessOptionsInstance: new ImplEnum.GenericEnum(
        ImplEnum.typeLegalProcessOptions,
    ),
    stateLegalProcessOptionsInstance: new ImplEnum.GenericEnum(
        ImplEnum.stateLegalProcessOptions,
    ),
    phaseLegalProcessOptionsInstance: new ImplEnum.GenericEnum(
        ImplEnum.phaseLegalProcessOptions,
    ),
    resultOptionsInstance: new ImplEnum.GenericEnum(ImplEnum.resultOptions),
};

const statusOptions = enumInstances.statusOptionsInstance.optionsList();
const yesOrNoOptions = enumInstances.yesOrNoOptionsInstance.optionsList();
const typeLegalProcessOptions =
    enumInstances.typeLegalProcessOptionsInstance.optionsList();
const phaseLegalProcessOptions =
    enumInstances.phaseLegalProcessOptionsInstance.optionsList();
const resultOptions = enumInstances.resultOptionsInstance.optionsList();

const Form: React.FC<{ settings: any }> = props => {
    const { settings } = props;
    const params = useParams<'id' | any>();
    const history = useHistory();
    const { user } = useAuth();
    const [totalValueRef, setTotalValueRef] = useState<number>();
    const [valueSentence, setValueSentence] = useState<number>();

    const { addToast } = useToast();

    const refForm =
        React.useRef<ImperativeHandleAccessedByParentComponent<IFormInput>>(
            null,
        );

    function calcPercentual({ value, percentual }: ICalcPercentual) {
        const percentualConverted = percentual / 100;

        return value * percentualConverted;
    }

    function calcTotalValue({
        honoraryValue,
        percentageHonoraryValue,
        succumbency,
        valueSentence,
    }: ICalcTotalValue) {
        let sum = 0;
        if (valueSentence && percentageHonoraryValue) {
            sum += calcPercentual({
                value: valueSentence,
                percentual: percentageHonoraryValue,
            });
        }
        if (valueSentence && succumbency) {
            sum += calcPercentual({
                value: valueSentence,
                percentual: succumbency,
            });
        }
        if (honoraryValue) {
            sum += honoraryValue;
        }
        return sum;
    }

    function getValue({ key }: IGetValue) {
        if (refForm?.current?.watch(key as Path<IFormInput>)) {
            return Number(refForm.current.watch(key as Path<IFormInput>));
        }
        return 0;
    }

    function callbackSuccess(response: any) {
        if (params?.id && response.data?.totalValue != totalValueRef) {
            response.data.generateInstallments = true;
        }
        if (
            response.data?.generateInstallments &&
            response.data?.totalValue > 0 &&
            response.data?.additionalHonorarium?.id &&
            response.data?.user?.id
        ) {
            const redirect = Object.keys(paths).find(k => k === 'legalProcess');
            history.push(
                `${paths.invoiceDetailRegister}/${
                    response.data.additionalHonorarium.id
                }/additionalHonorarium/${
                    refForm?.current?.watch('user')?.id
                }/${redirect}`,
            );
        } else {
            history.push(`${paths.legalProcess}?`);
        }
    }

    const defaultValues = React.useMemo<any>(() => {
        return {
            user: {
                id: user.sub,
                description: user.name,
            },
            generateInstallments: !params?.id,
        };
    }, [user, params]);

    const inputsForm = React.useMemo<FormInputProps<IFormInput>[]>(
        () => [
            {
                typeInput: 'component',
                name: 'code',
                control: null,
                label: 'Código',
                md: 12,
                xs: 12,
                componentProps: {
                    componentData: data => (
                        <Typography
                            sx={{
                                // flex: '1 1 100%',
                                fontSize: '1rem',
                                padding: 0,
                                margin: 0,
                            }}
                            variant="h6"
                            id="tableTitle"
                            component="div">
                            {data?.code ? `Código: ${data['code']}` : ''}
                        </Typography>
                    ),
                    conditionRender: params && params.id,
                },
            },
            {
                typeInput: 'text',
                name: 'processNumber',
                control: null,
                label: 'Número do processo',
                loadingAutocomplete: false,
                md: 4,
                xs: 12,
                rules: {
                    required: true,
                },
                messagesError: [
                    {
                        type: 'required',
                        message: 'O campo número do processo é obrigatório',
                    },
                ],
            },
            {
                typeInput: 'autocomplete',
                name: 'originType',
                control: null,
                label: 'Tipo de ação',
                md: 4,
                xs: 12,
                rules: {
                    required: true,
                },
                messagesError: [
                    {
                        type: 'required',
                        message: 'O campo é obrigatório',
                    },
                ],
                query: (value: string) => `origin-type?description=${value}`,
                activeDebounce: true,
            },
            {
                typeInput: 'autocomplete',
                name: 'customer',
                control: null,
                label: 'Cliente',
                md: 4,
                xs: 12,
                rules: {
                    required: true,
                },
                messagesError: [
                    {
                        type: 'required',
                        message: 'O campo cliente é obrigatório',
                    },
                ],
                query: (value: string) => `customer?name=${value}`,
                activeDebounce: true,
            },
            {
                typeInput: 'text',
                name: 'description',
                control: null,
                label: 'Descrição (opcional)',
                loadingAutocomplete: false,
                md: 6,
                xs: 12,
            },
            {
                typeInput: 'autocomplete',
                name: 'qualification',
                control: null,
                label: 'Qualificação',
                md: 3,
                xs: 12,
                rules: {
                    required: true,
                },
                messagesError: [
                    {
                        type: 'required',
                        message: 'O campo é obrigatório',
                    },
                ],
                query: (value: string) => `qualifications?description=${value}`,
                activeDebounce: true,
            },
            {
                typeInput: 'autocomplete',
                name: 'user',
                control: null,
                label: 'Advogado',
                md: 3,
                xs: 12,
                rules: {
                    required: true,
                },
                messagesError: [
                    {
                        type: 'required',
                        message: 'O campo Advogado(a) é obrigatório',
                    },
                ],
                query: (value: string) => `users?name=${value}`,
                activeDebounce: true,
            },
            {
                typeInput: 'number',
                name: 'valueCause',
                control: null,
                label: 'Valor da causa',
                md: 3,
                xs: 12,
                decimalScale: 2,
                rules: {
                    validate: (value: number) => {
                        if (value && value < 0) {
                            return 'O campo valor não pode ser menor que zero';
                        }
                    },
                },
                handleChange: (value: any) => {
                    if (!valueSentence) {
                        refForm?.current?.setValueInput({
                            value: value,
                            field: 'valueSentence',
                        });
                    }
                },
                handleOnBlur: (data: any) => {
                    setValueSentence(data);
                },
            },
            {
                typeInput: 'number',
                name: 'valueSentence',
                control: null,
                label: 'Valor do acordo / sentença',
                md: 3,
                xs: 12,
                decimalScale: 2,
                handleChange: (value: any) => {
                    let sum = calcTotalValue({
                        honoraryValue: getValue({
                            key: 'honoraryValue',
                        }),
                        percentageHonoraryValue: getValue({
                            key: 'percentageHonoraryValue',
                        }),
                        succumbency: getValue({ key: 'succumbency' }),
                        valueSentence: value,
                    });
                    if (sum) {
                        refForm?.current?.setValueInput({
                            value: sum,
                            field: 'totalValue',
                        });
                    }
                },
            },
            {
                typeInput: 'number',
                name: 'honoraryValue',
                control: null,
                label: 'Valor em honorários (R$)',
                md: 3,
                xs: 12,
                decimalScale: 2,
                handleChange: (value: any) => {
                    let sum = calcTotalValue({
                        honoraryValue: value,
                        percentageHonoraryValue: getValue({
                            key: 'percentageHonoraryValue',
                        }),
                        succumbency: getValue({ key: 'succumbency' }),
                        valueSentence: getValue({ key: 'valueSentence' }),
                    });
                    if (sum) {
                        refForm?.current?.setValueInput({
                            value: sum,
                            field: 'totalValue',
                        });
                    }
                },
            },
            {
                typeInput: 'number',
                name: 'percentageHonoraryValue',
                control: null,
                label: 'Valor em honorários (%)',
                md: 3,
                xs: 12,
                decimalScale: 2,
                rules: {
                    validate: (value: number) => {
                        if (value && value < 0) {
                            return 'O campo valor não pode ser menor que zero';
                        }
                    },
                },
                handleChange: (value: any) => {
                    let sum = calcTotalValue({
                        honoraryValue: getValue({
                            key: 'honoraryValue',
                        }),
                        percentageHonoraryValue: value,
                        succumbency: getValue({ key: 'succumbency' }),
                        valueSentence: getValue({ key: 'valueSentence' }),
                    });
                    if (sum) {
                        refForm?.current?.setValueInput({
                            value: sum,
                            field: 'totalValue',
                        });
                    }
                },
            },
            {
                typeInput: 'number',
                name: 'succumbency',
                control: null,
                label: 'Sucumbências (%)',
                md: 3,
                xs: 12,
                decimalScale: 2,
                handleChange: (value: any) => {
                    let sum = calcTotalValue({
                        honoraryValue: getValue({
                            key: 'honoraryValue',
                        }),
                        percentageHonoraryValue: getValue({
                            key: 'percentageHonoraryValue',
                        }),
                        succumbency: value,
                        valueSentence: getValue({ key: 'valueSentence' }),
                    });
                    if (sum) {
                        refForm?.current?.setValueInput({
                            value: sum,
                            field: 'totalValue',
                        });
                    }
                },
            },
            {
                typeInput: 'number',
                name: 'totalValue',
                control: null,
                label: 'Total em honorários',
                md: 3,
                xs: 12,
                decimalScale: 2,
            },
            {
                typeInput: 'autocomplete',
                name: 'typeLegalProcess',
                control: null,
                label: 'Tipo',
                loadingAutocomplete: false,
                options: typeLegalProcessOptions,
                md: 3,
                xs: 12,
                rules: {
                    validate: (value: any) => {
                        if (!value?.id) {
                            return 'O campo é obrigatório';
                        }
                    },
                },
                dto: data => {
                    data.typeLegalProcess = data.typeLegalProcess.id;
                    return data;
                },
                setData: data => {
                    return enumInstances.typeLegalProcessOptionsInstance.getObject(
                        data.typeLegalProcess,
                    );
                },
            },
            {
                typeInput: 'autocomplete',
                name: 'phase',
                control: null,
                label: 'Fase',
                loadingAutocomplete: false,
                options: phaseLegalProcessOptions,
                md: 3,
                xs: 12,
                rules: {
                    validate: (value: any) => {
                        if (!value?.id) {
                            return 'O campo é obrigatório';
                        }
                    },
                },
                dto: data => {
                    data.phase = data.phase.id;
                    return data;
                },
                setData: data => {
                    return enumInstances.phaseLegalProcessOptionsInstance.getObject(
                        data.phase,
                    );
                },
            },
            {
                typeInput: 'date',
                name: 'deadlineDate',
                control: null,
                label: 'Data do prazo',
                md: 3,
                xs: 12,
                placeholder: 'dd/mm/aaaa hh:mm',
                rules: {
                    validate: (value: any) => {
                        if (value && String(value) === 'Invalid Date') {
                            return 'A data está em um formato inválido';
                        }
                    },
                },
            },
            {
                typeInput: 'autocomplete',
                name: 'openTerm',
                control: null,
                label: 'Prazo em aberto?',
                loadingAutocomplete: false,
                setValue: null,
                options: yesOrNoOptions,
                md: 3,
                xs: 12,
                rules: {
                    validate: (value: any) => {
                        if (!value?.id) {
                            return 'O campo é obrigatório';
                        }
                    },
                },
                dto: data => {
                    const configResult = {
                        '0': false,
                        '1': true,
                    };

                    data.openTerm = configResult[data.openTerm.id as '0' | '1'];
                    return data;
                },
                setData: data => {
                    let result = '0';
                    if (data.openTerm) {
                        result = '1';
                    }
                    return enumInstances.yesOrNoOptionsInstance.getObject(
                        result,
                    );
                },
            },
            {
                typeInput: 'autocomplete',
                name: 'status',
                control: null,
                label: 'Status',
                loadingAutocomplete: false,
                options: statusOptions,
                md: 3,
                xs: 12,
                rules: {
                    validate: (value: any) => {
                        if (!value?.id) {
                            return 'O campo status é obrigatório';
                        }
                    },
                },
                dto: data => {
                    data.status = data.status.id;
                    return data;
                },
                handleChange: (
                    _: React.ChangeEvent<HTMLInputElement>,
                    option: any,
                ) => {
                    const configResult = {
                        '01': '1',
                        '02': '0',
                    };

                    let openTerm = configResult[option.id as '01' | '02'];

                    if (openTerm === '0') {
                        const resultEnum =
                            enumInstances.yesOrNoOptionsInstance.getObject(
                                openTerm,
                            );

                        refForm?.current?.setValue('openTerm', resultEnum);
                    }
                },
                setData: data => {
                    return enumInstances.statusOptionsInstance.getObject(
                        data.status,
                    );
                },
            },
            {
                typeInput: 'autocomplete',
                name: 'instance',
                control: null,
                label: 'Instância',
                md: 3,
                xs: 12,
                rules: {
                    required: true,
                },
                messagesError: [
                    {
                        type: 'required',
                        message: 'O campo é obrigatório',
                    },
                ],
                query: (value: string) => `instances?description=${value}`,
                activeDebounce: true,
            },
            {
                typeInput: 'autocomplete',
                name: 'legalProcessDistrict',
                control: null,
                label: 'Comarca',
                md: 3,
                xs: 12,
                rules: {
                    required: true,
                },
                messagesError: [
                    {
                        type: 'required',
                        message: 'O campo é obrigatório',
                    },
                ],
                query: (value: string) =>
                    `legal-process-districts?description=${value}`,
                activeDebounce: true,
            },
            {
                typeInput: 'autocomplete',
                name: 'stick',
                control: null,
                label: 'Vara',
                md: 3,
                xs: 12,
                rules: {
                    required: true,
                },
                messagesError: [
                    {
                        type: 'required',
                        message: 'O campo é obrigatório',
                    },
                ],
                query: (value: string) => `sticks?description=${value}`,
                activeDebounce: true,
            },
            {
                typeInput: 'date',
                name: 'startDate',
                control: null,
                label: 'Data de início do processo',
                md: 3,
                xs: 12,
                placeholder: 'dd/mm/aaaa hh:mm',
                rules: {
                    validate: (value: any) => {
                        if (value && String(value) === 'Invalid Date') {
                            return 'A data está em um formato inválido';
                        }
                    },
                },
            },
            {
                typeInput: 'date',
                name: 'endDate',
                control: null,
                label: 'Data de fim do processo',
                md: 3,
                xs: 12,
                placeholder: 'dd/mm/aaaa hh:mm',
                rules: {
                    validate: (value: any) => {
                        if (value && String(value) === 'Invalid Date') {
                            return 'A data está em um formato inválido';
                        }
                    },
                },
            },
            {
                typeInput: 'autocomplete',
                name: 'resultLegalProcess',
                control: null,
                label: 'Resultado',
                loadingAutocomplete: false,
                options: resultOptions,
                md: 3,
                xs: 12,
                rules: null,
                dto: data => {
                    if (data.resultLegalProcess?.id) {
                        data.resultLegalProcess = data.resultLegalProcess.id;
                    }
                    return data;
                },
                setData: data => {
                    return enumInstances.resultOptionsInstance.getObject(
                        data.resultLegalProcess,
                    );
                },
            },
            {
                typeInput: 'text',
                name: 'link',
                control: null,
                label: 'Link do processo',
                loadingAutocomplete: false,
                md: 6,
                xs: 12,
            },
            {
                typeInput: 'text',
                name: 'linkDocuments',
                control: null,
                label: 'Link para pasta de documentos',
                loadingAutocomplete: false,
                md: 6,
                xs: 12,
            },
            {
                typeInput: 'text',
                multiline: true,
                name: 'observation',
                control: null,
                label: 'Observações',
                loadingAutocomplete: false,
                md: 12,
                xs: 12,
            },
            {
                typeInput: 'checkbox',
                name: 'generateInstallments',
                control: null,
                label: 'Gerar parcelas',
                md: 3,
                xs: 12,
            },
        ],
        [params, valueSentence],
    );

    const implSetValueData = useCallback((data: IFormInput) => {
        for (const input of inputsForm) {
            if (input.name === 'totalValue') {
                setTotalValueRef(
                    data[input.name as keyof IFormInput] as number,
                );
            }
            if (input.name == 'generateInstallments' && !params?.id) {
                refForm?.current?.setValue(
                    input.name as Path<IFormInput>,
                    true,
                );
                continue;
            }
            if (input?.setData) {
                refForm?.current?.setValue(
                    input.name as Path<IFormInput>,
                    input.setData(data),
                );
                continue;
            }

            refForm?.current?.setValue(
                input.name as Path<IFormInput>,
                data[input.name as keyof IFormInput],
            );
        }
        if (data['valueSentence']) {
            setValueSentence(data['valueSentence']);
        }
    }, []);

    function validation(data: IFormInput) {
        if (data?.totalValue && (!data?.resultLegalProcess || !data?.endDate)) {
            addToast({
                type: 'warn',
                title: 'Falta de informações',
                description:
                    'Como o Total em honorários foi informado, o Resultado e a Data de fim do processo precisam ser informados',
            });
            return false;
        }

        return true;
    }

    return (
        <FormComplete
            defaultValues={defaultValues}
            inputsForm={inputsForm}
            ref={refForm}
            settings={settings}
            callbackSuccess={callbackSuccess}
            implSetValueData={implSetValueData}
            validation={validation}
        />
    );
};
export default Form;
